<template>
	<DashboardItem :header="$t('dashboard.userPulseStats.headers.top')" :timing="$t('dashboard.generalTiming')">
		<Row>
			<Col v-if="!expanded" :span="12">
				<UserPulseStatsActiveUsers :data="dataActiveUsers" :loading="isLoading" :updateEventsList="updateActiveUsersList" class="active-users"/>
				<div class="flex-wrapper align-baseline">
					<TypographyTitle :level="5" class="nobr">{{ $t('dashboard.userPulseStats.headers.eventGroups') }}</TypographyTitle>
					<InputNumber
						v-model:value="period"
						:min="1"
						:max="24"
						:precision="0"
						class="period-input"
						@change="sessionStore.updateUserPulseStatsPeriod"
					/>
					<TypographyTitle :level="5" class="nobr">{{ $t('dashboard.userPulseStats.period', period) }}</TypographyTitle>
				</div>
				<UserPulseStatsEventsItem :data="dataNear" service="near" :loading="isLoading" :updateEventsList="updateEventsList" />
				<UserPulseStatsEventsItem :data="dataVrs" service="vrs" :loading="isLoading" :updateEventsList="updateEventsList" />
			</Col>
			<Col :span="expanded ? 24 : 11" :offset="expanded ? 0 : 1">
				<div class="flex-wrapper align-baseline">
					<TypographyTitle :level="5" class="flex-grow">{{ $t(eventsListTitle) }}</TypographyTitle>
					<Tooltip :title="$t('dashboard.userPulseStats.expandList')">
						<Button shape="circle" size="small" class="expand-button" @click="toggleExpanded">
							<template #icon>
								<FullscreenExitOutlined v-if="expanded" />
								<FullscreenOutlined v-else />
							</template>
						</Button>
					</Tooltip>
				</div>
				<VerticalLayout>
					<LabeledSwitch
						v-model="parseItems"
						:small="true"
						:rightLabel="$t('dashboard.userPulseStats.parseItems')"
					/>
					<div v-bind="containerProps" class="events-container">
						<div v-bind="wrapperProps">
							<UserPulseLogItem v-for="item in list" :key="item.index" :item="item" :parseItems="parseItems" :activeUsersMode="activeUsersMode" />
						</div>
					</div>
				</VerticalLayout>
			</Col>
		</Row>
	</DashboardItem>
</template>

<script>
import { ref, watch } from 'vue';
import { TypographyTitle, Col, Row, Button, InputNumber, Tooltip } from 'ant-design-vue';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';

import { useSessionStore } from '@/stores/session';

import { refDebounced, useVirtualList } from '@vueuse/core';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';

import { showErrorMessage } from '@/components/common/errorMessage';

import DashboardItem from '@/components/dashboard/DashboardItem.vue';
import UserPulseLogItem from '@/components/dashboard/userPulse/UserPulseLogItem.vue';
import UserPulseStatsActiveUsers from '@/components/dashboard/userPulse/UserPulseStatsActiveUsers.vue';
import UserPulseStatsEventsItem from '@/components/dashboard/userPulse/UserPulseStatsEventsItem.vue';

export default {
	components: {
		Row, Col, TypographyTitle, InputNumber, Button, Tooltip,
		LabeledSwitch, VerticalLayout,
		FullscreenOutlined, FullscreenExitOutlined,
		DashboardItem, UserPulseStatsEventsItem, UserPulseStatsActiveUsers, UserPulseLogItem,
	},
	setup(props, {expose}) {
		const isLoading = ref(false);
		const isListLoading = ref(false);
		const sessionStore = useSessionStore();
		const period = ref(1);
		// пришлось сделать через watch, потому что refDebounced не работает с pinia
		watch(() => sessionStore.userPulseStatsPeriod, (newPeriod) => {
			period.value = newPeriod;
		}, { immediate: true });
		const periodDebounced = refDebounced(period, 500);
		const dataActiveUsers = ref({});
		const dataActiveUsersDetails = ref({});
		const dataNear = ref({});
		const dataVrs = ref({});
		const firstId = ref(0);
		const lastId = ref(0);
		const expanded = ref(false);
		const parseItems = ref(true);
		const activeUsersMode = ref(false);
		const eventsList = ref([]);
		const eventsListTitle = ref('dashboard.userPulseStats.headers.eventsList');
		const refresh = () => {
			isLoading.value = true;
			Api.dashboardUserPulseStatsGet(periodDebounced.value, response => {
				if (response.status === 'ok') {
					dataActiveUsers.value = {
						near: response.near.active,
						vrs: response.vrs.active,
					}
					dataActiveUsersDetails.value = {
						near: response.near.active_details,
						vrs: response.vrs.active_details,
					}
					dataNear.value = response.near;
					dataVrs.value = response.vrs;
					firstId.value = response.first_id;
					lastId.value = response.last_id;
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
			});
		};
		watch(periodDebounced, () => {
			refresh();
		});
		const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(eventsList, { itemHeight: 22 });
		const updateActiveUsersList = (service) => {
			eventsList.value = dataActiveUsersDetails.value[service];
			eventsListTitle.value = 'dashboard.userPulseStats.headers.activeUsersList';
			activeUsersMode.value = true;
			scrollTo(0);
		}
		const updateEventsList = (event, service) => {
			eventsList.value = [];
			isListLoading.value = true;
			Api.dashboardUserPulseEventsListGet(event, service, firstId.value, lastId.value, response => {
				if (response.status === 'ok') {
					eventsList.value = response.events;
					eventsListTitle.value = 'dashboard.userPulseStats.eventGroups.' + event;
					activeUsersMode.value = false;
					scrollTo(0);
				} else {
					showErrorMessage(response.errors);
				}
				isListLoading.value = false;
			});
		}
		const toggleExpanded = () => {
			expanded.value = !expanded.value;
		}
		expose({ refresh });
		return {
			isLoading,
			isListLoading,
			sessionStore,
			period,
			dataActiveUsers,
			dataNear,
			dataVrs,
			lastId,
			firstId,
			expanded,
			parseItems,
			eventsListTitle,
			activeUsersMode,
			list,
			containerProps,
			wrapperProps,
			updateActiveUsersList,
			updateEventsList,
			toggleExpanded,
		};
	},
}
</script>

<style scoped>
.active-users {
	margin-bottom: 24px;
}
.period-input {
	margin-inline: 8px;
	flex: 0 0 60px;
}
.events-container {
	border: 1px solid #d9d9d9;
	padding: 10px;
	height: 480px;
	background: white;
}
.expand-button {
	margin-inline-start: 8px;
}
</style>
